import { useMemo } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client/react';
import { Button, Modal, Tooltip } from 'antd';
import {
  ExclamationCircleOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons';

import AlertTooltip from 'components/AlertTooltip/AlertTooltip';
import SectionMessage from 'components/SectionMessage/SectionMessage';

import { CUSTOMER_OVERVIEW } from 'queries/customer';
import { ACTIVATE_CARD } from 'mutations/activateCard';

import useAnalytics from 'hooks/useAnalytics';
import useTimeoutValue from 'hooks/useTimeoutValue';
import useAccountQuery from 'hooks/useAccountQuery';
import {
  ANALYTICS_EVENTS,
  CARD_ISSUANCE_STATE_STATUS,
  STATUS,
  SIZE,
} from 'utilities/constants';

const { confirm } = Modal;

const ActivateCardButton = () => {
  const { accountId, customerId } = useParams();
  const { trackEvent } = useAnalytics();

  const [activationSuccess, setActivationSuccess] = useTimeoutValue(
    false,
    2500,
  );
  const [showActivationError, setShowActivationError] = useTimeoutValue(
    false,
    5000,
  );

  const { account } = useAccountQuery(customerId, accountId);

  const customerOverviewQuery = useQuery(CUSTOMER_OVERVIEW, {
    variables: { customerId },
    notifyOnNetworkStatusChange: true,
  });

  const [dateOfBirth, zipCode, ssnLast4] = useMemo(() => {
    let dateOfBirth,
      zipCode,
      ssnLast4 = '';
    const customer = customerOverviewQuery.data?.customer;
    if (customerOverviewQuery.data) {
      dateOfBirth = customer.birthDate;
      ssnLast4 = customer.lastFourSsn;
      zipCode = customer.contactInfo.address.postalCode.substring(0, 5);
    }
    return [dateOfBirth, zipCode, ssnLast4];
  }, [customerOverviewQuery.data]);

  const [
    submitActivateCardRequest,
    { loading: submittingActivateCardRequest, error: submitActivateCardError },
  ] = useMutation(ACTIVATE_CARD, {
    refetchQueries: ['GetCustomerWithFullAccounts'],
    onCompleted: () => {
      setActivationSuccess(true);
    },
    onError: () => {
      setShowActivationError(true);
    },
  });

  const cardLast4 = account?.cardDetails?.last4;

  const customerVerificationDetailsLoaded =
    ssnLast4 && cardLast4 && dateOfBirth && zipCode;

  const issuanceState = account?.cardDetails?.issuanceState || '';

  const cardIsActivated =
    issuanceState === CARD_ISSUANCE_STATE_STATUS.ACTIVATED;
  const cardIsPreExpired =
    issuanceState === CARD_ISSUANCE_STATE_STATUS.PRE_EXPIRED;

  const cardIsNotMailedOrDelayed =
    issuanceState !== CARD_ISSUANCE_STATE_STATUS.MAILED &&
    issuanceState !== CARD_ISSUANCE_STATE_STATUS.DELAYED;

  const shouldDisplay = !cardIsActivated && !cardIsPreExpired;

  const { eligible: eligibleForActivation, reasons: disabledReasons } =
    account?.activationEligibilityV2 || {};

  let activationDisabledTooltipMessage = '';

  if (cardIsNotMailedOrDelayed) {
    activationDisabledTooltipMessage = `Card is ${issuanceState.toLowerCase()}`;
  } else if (disabledReasons?.length) {
    activationDisabledTooltipMessage = disabledReasons
      .map((reason) => `${reason.description} (${reason.reasonCode})`)
      .join(', ');
  }

  const handleActivateCardClick = async () => {
    await submitActivateCardRequest({
      variables: {
        customerId,
        accountId,
        ssnLast4,
        cardLast4,
        dateOfBirth,
        zipCode,
      },
    });

    trackEvent(ANALYTICS_EVENTS.ACTIVATE_CARD_SUBMIT);
  };

  const confirmCardActivation = () => {
    confirm({
      title: 'Are you sure you want to activate this card?',
      icon: <ExclamationCircleFilled />,
      okText: 'Yes',
      onOk() {
        return handleActivateCardClick();
      },
      onCancel() {},
    });
  };

  const disabled = !eligibleForActivation || !customerVerificationDetailsLoaded;

  return shouldDisplay ? (
    <>
      <AlertTooltip
        type="error"
        placement="topLeft"
        open={showActivationError}
        zIndex={4}
        title={
          submitActivateCardError &&
          `Unable to activate card due to: ${submitActivateCardError.message}. Please escalate to TSYS team via slack`
        }
      >
        <Tooltip
          title={!eligibleForActivation ? activationDisabledTooltipMessage : ''}
        >
          <Button
            color="orange"
            disabled={disabled}
            icon={<ExclamationCircleOutlined />}
            loading={submittingActivateCardRequest}
            onClick={confirmCardActivation}
            style={{
              boxShadow: !disabled ? ' 0 2px 0 var(--orange-10)' : null,
            }}
            size="small"
          >
            Activate Card
          </Button>
        </Tooltip>
      </AlertTooltip>
      <SectionMessage
        data-testid="activate-card-success-message"
        status={STATUS.SUCCESS}
        size={SIZE.XS}
        text="Card Successfully Activated"
        cover={true}
        visible={activationSuccess}
      />
    </>
  ) : undefined;
};

export default ActivateCardButton;
